<template>
  <ts-loading-banner :loading="saving || loadingDetail">
    <div class="ts-space-y-4 bank-v2-form">
      <div class="ts-grid ts-grid-cols-1 lg:ts-grid-cols-12 ts-gap-4">
        <section class="bank-form-panel lg:ts-col-span-8">
          <div class="bank-form-panel__header">
            <div class="bank-form-panel__title">
              <span class="bank-form-panel__icon">
                <i class="fas fa-landmark"></i>
              </span>
              {{ $t('bankV2.bankInformation') }}
            </div>
          </div>

          <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 ts-gap-3">
            <div>
              <label class="bank-form-label required">
                {{ $t('bankV2.branch') }}
              </label>
              <a-select
                v-model="model.branch_id"
                allow-clear
                show-search
                class="ts-w-full"
                :placeholder="$t('bankV2.branchPlaceholder')"
                :filter-option="filterSelectOption"
              >
                <a-select-option
                  v-for="branch in options.branches"
                  :key="branch.branch_id"
                  :value="branch.branch_id"
                >
                  {{ branch.branch_name_en }}
                </a-select-option>
              </a-select>
              <div v-if="errors.has('branch_id')" class="bank-form-error">
                {{ errors.first('branch_id') }}
              </div>
            </div>

            <div>
              <label class="bank-form-label required">
                {{ $t('bankV2.bankName') }}
              </label>
              <a-input
                v-model="model.bank_name"
                :placeholder="$t('bankV2.bankNamePlaceholder')"
              >
                <i slot="prefix" class="fas fa-university"></i>
              </a-input>
              <div v-if="errors.has('bank_name')" class="bank-form-error">
                {{ errors.first('bank_name') }}
              </div>
            </div>

            <div>
              <label class="bank-form-label">
                {{ $t('bankV2.bankNameKh') }}
              </label>
              <a-input
                v-model="model.bank_name_kh"
                :placeholder="$t('bankV2.bankNameKhPlaceholder')"
              >
                <i slot="prefix" class="fas fa-language"></i>
              </a-input>
            </div>

            <div>
              <label class="bank-form-label">
                {{ $t('bankV2.shortName') }}
              </label>
              <a-input
                v-model="model.short_name"
                :placeholder="$t('bankV2.shortNamePlaceholder')"
              >
                <i slot="prefix" class="fas fa-tag"></i>
              </a-input>
            </div>

            <div v-if="canManageKhqrConfig">
              <label class="bank-form-label">
                {{ $t('bankV2.khqrProvider') }}
              </label>
              <a-select
                v-model="model.khqr_provider"
                allow-clear
                class="ts-w-full"
                :placeholder="$t('bankV2.khqrProviderPlaceholder')"
                @change="onKhqrProviderChange"
              >
                <a-select-option
                  v-for="provider in options.khqr_providers"
                  :key="provider.value"
                  :value="provider.value"
                >
                  {{ provider.label }}
                </a-select-option>
              </a-select>
              <div class="bank-form-hint">
                {{ $t('bankV2.khqrProviderHint') }}
              </div>
            </div>

            <div class="bank-checkbox-field">
              <label class="bank-form-label bank-form-label--spacer"
                >&nbsp;</label
              >
              <div class="bank-checkbox-line">
                <ts-checkbox v-model="model.is_active">
                  {{ $t('bankV2.active') }}
                </ts-checkbox>
              </div>
            </div>

            <div class="md:ts-col-span-2">
              <label class="bank-form-label">
                {{ $t('bankV2.paymentProfiles') }}
              </label>
              <a-checkbox-group
                v-model="model.payment_profile_ids"
                class="bank-payment-profile-group"
              >
                <a-checkbox
                  v-for="profile in options.payment_profiles"
                  :key="profile.id"
                  :value="profile.id"
                >
                  {{ profile.name }}
                </a-checkbox>
              </a-checkbox-group>
            </div>
          </div>
        </section>

        <section class="bank-form-panel lg:ts-col-span-4">
          <div class="bank-form-panel__header">
            <div class="bank-form-panel__title">
              <span class="bank-form-panel__icon">
                <i class="fas fa-image"></i>
              </span>
              {{ $t('bankV2.logo') }}
            </div>
            <span class="bank-logo-size">{{ $t('bankV2.logoSize') }}</span>
          </div>

          <div class="bank-logo-uploader">
            <ts-image-input
              :file="logoPreview"
              :width="150"
              :height="150"
              crop
              @input="setLogo"
            >
              <template slot="placeholder">
                <i class="fas fa-university"></i>
              </template>
            </ts-image-input>
          </div>
          <div class="bank-form-hint ts-text-center">
            {{ $t('bankV2.logoHint') }}
          </div>
        </section>
      </div>

      <section v-if="canManageKhqrConfig && model.khqr_provider" class="bank-form-panel">
        <div class="bank-form-panel__header">
          <div class="bank-form-panel__title">
            <span class="bank-form-panel__icon">
              <i class="fas fa-key"></i>
            </span>
            {{ $t('bankV2.khqrConfig') }}
          </div>
        </div>

        <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 ts-gap-3">
          <div>
            <label class="bank-form-label">{{ $t('bankV2.tokenUrl') }}</label>
            <a-input v-model="model.khqr_config.token_url" />
          </div>
          <div>
            <label class="bank-form-label">{{ $t('bankV2.initUrl') }}</label>
            <a-input v-model="model.khqr_config.init_url" />
          </div>
          <div>
            <label class="bank-form-label">{{ $t('bankV2.basicToken') }}</label>
            <a-input-password v-model="model.khqr_config.basic_token" />
          </div>
          <div>
            <label class="bank-form-label">{{ $t('bankV2.merchantId') }}</label>
            <a-input v-model="model.khqr_config.merchant_id" />
          </div>
          <div>
            <label class="bank-form-label">{{
              $t('bankV2.merchantCity')
            }}</label>
            <a-input v-model="model.khqr_config.merchant_city" />
          </div>
          <div>
            <label class="bank-form-label">{{
              $t('bankV2.merchantCategory')
            }}</label>
            <a-input v-model="model.khqr_config.merchant_category" />
          </div>
          <div>
            <label class="bank-form-label">{{
              $t('bankV2.countryCode')
            }}</label>
            <a-input v-model="model.khqr_config.country_code" />
          </div>
          <div class="ts-grid ts-grid-cols-2 ts-gap-3">
            <div>
              <label class="bank-form-label">{{ $t('bankV2.timeout') }}</label>
              <a-input-number
                v-model="model.khqr_config.timeout"
                class="ts-w-full"
                :min="1"
                :max="120"
              />
            </div>
            <div>
              <label class="bank-form-label">{{
                $t('bankV2.tokenCacheTtl')
              }}</label>
              <a-input-number
                v-model="model.khqr_config.token_cache_ttl"
                class="ts-w-full"
                :min="60"
                :max="86400"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="bank-form-panel">
        <div class="bank-form-panel__header">
          <div class="bank-form-panel__title">
            <span class="bank-form-panel__icon">
              <i class="fas fa-wallet"></i>
            </span>
            {{ $t('bankV2.accounts') }}
            <span class="bank-account-count">{{ model.accounts.length }}</span>
          </div>
          <a-button class="bank-add-account-button" @click="addAccount">
            <i class="fas fa-plus"></i>
            {{ $t('bankV2.addAccount') }}
          </a-button>
        </div>

        <div class="ts-space-y-2">
          <div
            v-for="(account, index) in model.accounts"
            :key="index"
            class="bank-account-card"
          >
            <div class="bank-account-card__header">
              <span class="bank-account-title">
                {{ $t('bankV2.account') }} #{{ index + 1 }}
              </span>
              <button
                type="button"
                class="bank-account-remove"
                :title="$t('bankV2.removeAccount')"
                @click="removeAccount(index)"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 ts-gap-2">
              <div>
                <label class="bank-form-label">{{
                  $t('bankV2.currency')
                }}</label>
                <a-select
                  v-model="account.currency_id"
                  allow-clear
                  class="ts-w-full"
                  :placeholder="$t('bankV2.currencyPlaceholder')"
                >
                  <a-select-option
                    v-for="currency in options.currencies"
                    :key="currency.currency_id"
                    :value="currency.currency_id"
                  >
                    {{ currency.currency_code }}
                  </a-select-option>
                </a-select>
              </div>

              <div>
                <label class="bank-form-label required">
                  {{ $t('bankV2.accountName') }}
                </label>
                <a-input
                  v-model="account.account_name"
                  :placeholder="$t('bankV2.accountNamePlaceholder')"
                />
                <div
                  v-if="errors.has(`accounts.${index}.account_name`)"
                  class="bank-form-error"
                >
                  {{ errors.first(`accounts.${index}.account_name`) }}
                </div>
              </div>

              <div>
                <label class="bank-form-label required">
                  {{ $t('bankV2.accountNumber') }}
                </label>
                <a-input
                  v-model="account.account_number"
                  :placeholder="$t('bankV2.accountNumberPlaceholder')"
                />
                <div
                  v-if="errors.has(`accounts.${index}.account_number`)"
                  class="bank-form-error"
                >
                  {{ errors.first(`accounts.${index}.account_number`) }}
                </div>
              </div>

              <div>
                <label class="bank-form-label">{{
                  $t('bankV2.accountType')
                }}</label>
                <a-input
                  v-model="account.account_type"
                  :placeholder="$t('bankV2.accountTypePlaceholder')"
                />
              </div>

              <div>
                <label class="bank-form-label">{{
                  $t('bankV2.swiftCode')
                }}</label>
                <a-input
                  v-model="account.swift_code"
                  :placeholder="$t('bankV2.swiftCode')"
                />
              </div>

              <div class="bank-checkbox-field">
                <label class="bank-form-label bank-form-label--spacer"
                  >&nbsp;</label
                >
                <div class="bank-checkbox-line bank-checkbox-line--inline">
                  <ts-checkbox v-model="account.is_active">
                    {{ $t('bankV2.active') }}
                  </ts-checkbox>
                  <a-tooltip
                    v-if="canManageKhqrConfig"
                    :title="
                      !model.khqr_provider
                        ? $t('bankV2.khqrProviderRequiredHint')
                        : ''
                    "
                  >
                    <ts-checkbox
                      v-model="account.is_khqr_enabled"
                      :disabled="!model.khqr_provider"
                    >
                      {{ $t('bankV2.khqrEnabled') }}
                    </ts-checkbox>
                  </a-tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="errors.has('accounts')" class="bank-form-error ts-mt-2">
          {{ errors.first('accounts') }}
        </div>
      </section>

      <section class="bank-form-panel">
        <label class="bank-form-label">{{ $t('bankV2.remark') }}</label>
        <a-textarea v-model="model.remark" :rows="2" />
      </section>

      <div
        :class="variant === 'modal' ? 'bank-modal-footer' : 'bank-page-footer'"
      >
        <ts-button @click="cancel">{{ $t('bankV2.cancel') }}</ts-button>
        <ts-button
          v-if="mode === 'create'"
          outline
          color="primary"
          :disabled="saving"
          @click="save(true)"
        >
          {{ $t('bankV2.saveAddNew') }}
        </ts-button>
        <ts-button
          color="primary"
          :disabled="saving"
          @click="mode === 'edit' ? update() : save(false)"
        >
          {{ mode === 'edit' ? $t('bankV2.update') : $t('bankV2.save') }}
        </ts-button>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
import { Errors } from 'form-backend-validation'
import notificationAlert from '@/utils/NotificationAlert'
import { mapActions, mapState } from 'vuex'

const emptyKhqrConfig = () => ({
  token_url: null,
  init_url: null,
  basic_token: null,
  merchant_id: null,
  merchant_city: 'Phnom Penh',
  merchant_category: '8299',
  country_code: 'KH',
  timeout: 20,
  token_cache_ttl: 3300
})

const emptyAccount = () => ({
  account_id: null,
  account_number: null,
  account_name: null,
  currency_id: null,
  account_type: null,
  swift_code: null,
  remarks: null,
  is_active: true,
  is_khqr_enabled: false
})

const emptyModel = () => ({
  bank_id: null,
  bank_name: null,
  bank_name_kh: null,
  short_name: null,
  branch_id: null,
  bank_address: null,
  logo: null,
  logo_url: null,
  khqr_provider: null,
  khqr_config: emptyKhqrConfig(),
  remark: null,
  is_active: true,
  payment_profile_ids: [],
  accounts: [emptyAccount()]
})

export default {
  name: 'BankV2Form',
  props: {
    mode: {
      type: String,
      default: 'create'
    },
    bankId: {
      type: [String, Number],
      default: null
    },
    variant: {
      type: String,
      default: 'page'
    }
  },
  data() {
    return {
      saving: false,
      loadingDetail: false,
      errors: new Errors(),
      logoFile: null,
      model: emptyModel()
    }
  },
  computed: {
    ...mapState('bankV2', ['options']),
    logoPreview() {
      return this.logoFile || this.model.logo_url || this.model.logo
    },
    canManageKhqrConfig() {
      const user = this.$store.state.authUser || {}
      return user.is_supper === true || Number(user.is_supper) === 1
    }
  },
  mounted() {
    this.fetchOptions()
    if (this.mode === 'edit' && this.bankId) {
      this.loadRecord()
    }
  },
  methods: {
    ...mapActions('bankV2', [
      'fetchOptions',
      'find',
      'onSave',
      'onUpdate',
      'uploadLogo'
    ]),
    async loadRecord() {
      this.loadingDetail = true
      try {
        const response = await this.find(this.bankId)
        const data = response.data.data
        this.model = Object.assign(emptyModel(), data, {
          branch_id: data.branch_id ? Number(data.branch_id) : null,
          khqr_config: Object.assign(emptyKhqrConfig(), data.khqr_config || {}),
          payment_profile_ids: (data.payment_profile_ids || []).map(id =>
            Number(id)
          ),
          accounts:
            data.accounts && data.accounts.length
              ? data.accounts.map(account => ({
                  ...account,
                  account_id: account.account_id
                    ? Number(account.account_id)
                    : null,
                  currency_id: account.currency_id
                    ? Number(account.currency_id)
                    : null,
                  is_active: !!account.is_active,
                  is_khqr_enabled: !!account.is_khqr_enabled
                }))
              : [emptyAccount()]
        })
        this.logoFile = null
      } catch (error) {
        notificationAlert(error)
        this.cancel()
      } finally {
        this.loadingDetail = false
      }
    },
    async save(addNew) {
      this.saving = true
      this.errors = new Errors()
      try {
        await this.uploadLogoIfNeeded()
        const response = await this.onSave(this.model)
        notificationAlert(response, 'success')
        if (addNew) {
          this.model = emptyModel()
          this.logoFile = null
          return
        }
        this.finish()
      } catch (error) {
        notificationAlert(error)
        this.errors = new Errors((error.response || {}).data.errors || {})
      } finally {
        this.saving = false
      }
    },
    async update() {
      this.saving = true
      this.errors = new Errors()
      try {
        await this.uploadLogoIfNeeded()
        const response = await this.onUpdate(this.model)
        notificationAlert(response, 'success')
        this.finish()
      } catch (error) {
        notificationAlert(error)
        this.errors = new Errors((error.response || {}).data.errors || {})
      } finally {
        this.saving = false
      }
    },
    setLogo(file) {
      this.logoFile = file
    },
    async uploadLogoIfNeeded() {
      if (!(this.logoFile instanceof File || this.logoFile instanceof Blob)) {
        return
      }

      const formData = new FormData()
      formData.append('logo', this.logoFile)

      const response = await this.uploadLogo(formData)
      this.model.logo = response.data || null
      this.logoFile = null
    },
    finish() {
      if (this.variant === 'modal') {
        this.$emit('saved')
        return
      }
      this.$router.push({ name: 'bank-v2' })
    },
    cancel() {
      if (this.variant === 'modal') {
        this.$emit('cancel')
        return
      }
      this.$router.push({ name: 'bank-v2' })
    },
    addAccount() {
      this.model.accounts.push(emptyAccount())
    },
    removeAccount(index) {
      if (this.model.accounts.length <= 1) {
        return
      }
      this.model.accounts.splice(index, 1)
    },
    onKhqrProviderChange(value) {
      this.model.khqr_provider = value || null
      if (this.model.khqr_provider) {
        this.model.khqr_config = Object.assign(
          emptyKhqrConfig(),
          this.model.khqr_config || {}
        )
        return
      }
      this.model.khqr_config = emptyKhqrConfig()
      this.model.accounts = this.model.accounts.map(account =>
        Object.assign({}, account, { is_khqr_enabled: false })
      )
    },
    filterSelectOption(input, option) {
      return String(option.componentOptions.children[0].text || '')
        .toLowerCase()
        .includes(input.toLowerCase())
    }
  }
}
</script>

<style scoped>
.bank-form-panel {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 14px;
}

.bank-form-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.bank-form-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.bank-form-panel__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
}

.bank-form-label {
  display: block;
  margin-bottom: 4px;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
}

.bank-payment-profile-group {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 18px;
  min-height: 32px;
  align-items: center;
}

.bank-payment-profile-group .ant-checkbox-wrapper {
  margin-left: 0;
  margin-bottom: 0;
}

.bank-form-label--spacer {
  visibility: hidden;
}

.bank-checkbox-line {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.bank-checkbox-line--inline {
  flex-wrap: wrap;
  gap: 16px;
}

.bank-checkbox-line .ant-checkbox-wrapper {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0;
  line-height: 32px;
}

.bank-checkbox-line .ant-checkbox {
  top: 0;
}

.bank-form-error {
  margin-top: 3px;
  color: #ef4444;
  font-size: 11px;
}

.bank-form-hint {
  margin-top: 4px;
  color: #64748b;
  font-size: 11px;
  line-height: 16px;
}

.bank-logo-size,
.bank-account-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
}

.bank-account-count {
  background: #dbeafe;
  color: #1d4ed8;
}

.bank-logo-uploader {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px auto;
}

.bank-logo-uploader ::v-deep .file-input-container {
  overflow: hidden;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #94a3b8;
}

.bank-logo-uploader ::v-deep .svg-back {
  background: #f8fafc;
}

.bank-logo-uploader ::v-deep i {
  font-size: 42px;
}

.bank-logo-uploader ::v-deep img {
  object-fit: contain;
}

.bank-add-account-button {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px;
  height: 34px !important;
  padding: 0 14px !important;
  border-color: #bfdbfe !important;
  border-radius: 7px !important;
  background: #eff6ff !important;
  color: #1d4ed8 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.08);
}

.bank-add-account-button:hover,
.bank-add-account-button:focus {
  border-color: #93c5fd !important;
  background: #dbeafe !important;
  color: #1e40af !important;
}

.bank-account-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
}

.bank-account-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.bank-account-title {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
}

.bank-account-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fef2f2;
  color: #ef4444;
}

.bank-modal-footer,
.bank-page-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 0 0;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
}

.bank-modal-footer .ts-button,
.bank-modal-footer button {
  min-width: auto !important;
  height: 32px !important;
  padding: 0 12px !important;
  font-size: 12px !important;
}

.bank-page-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 66px;
  z-index: 20;
  padding: 12px 24px;
  box-shadow: 0 -8px 20px rgba(15, 23, 42, 0.08);
}
</style>
